import React from 'react';
import { ClientHttpRequester } from '@smarterservices/client-http-requester';
import { config, API_PATHS, MEDIA_API_PATHS } from '../config';
import { getMediaAuthToken } from '../helpers/utilities';
class ApiClient {
  constructor(options) {
    // Build our requester with default configs
    const requesterConfig = {
      apiUrl: config.proctoring.api.baseUrl,
      retry: {
        enabled: true,
      },
    };

    this.spToken = options.spToken;

    this.requester = ClientHttpRequester(requesterConfig);

    const mediaApiRequesterConfig = {
      apiUrl: config.media.playback.baseUrl,
      retry: {
        enabled: true,
      },
    };

    this.mediaRequester = ClientHttpRequester(mediaApiRequesterConfig);
  }

  /**
   * Build the headers for an API request
   * @param {Object} headers - the base headers
   * @returns {Object} - the headers with the auth token included
   */
  buildHeaders(headers = {}) {
    const authHeader = { token: this.spToken };
    return { ...headers, ...authHeader };
  }

  /**
   * Replaces path params with given values
   * @param {string} path - the path with params
   * @param {Object} params - the values to replace the params
   * @returns {string} - the path with params replaced
   */
  buildPath(path, params) {
    return path.replace(/:([^/]+)/g, (_, key) => {
      return key in params ? params[key] : `:${key}`;
    });
  }

  /**
   * Check and extend the TTL for a given token
   * @param {string} [token=this.spToken] - the token to use for the request
   * @returns {Promise<Object>} - a promise that resolves with the result of the request
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the data returned by the API
   * @prop {number} statusCode - the HTTP status code of the response
   * @prop {Object} error - the error object if the request failed
   */
  async checkAuthentication(token = this.spToken) {
    const pathReplacements = {};

    const url = this.buildPath(API_PATHS.EXTEND_TTL, pathReplacements);

    try {
      const response = await this.requester.get(url, { token });
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e.response.status, error: e };
    }
  }

  /**
   * Retrieve permissions for a given token
   * @param {string} [token=this.spToken] - the token to use for the request
   * @returns {Promise<Object>} - a promise that resolves with the result of the request
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the permissions data returned by the API
   * @prop {number} statusCode - the HTTP status code of the response (only present on failure)
   * @prop {Object} error - the error object if the request failed
   */
  async permissions(token = this.spToken) {
    const pathReplacements = {};

    const url = this.buildPath(API_PATHS.TOKEN_PERMISSIONS, pathReplacements);

    try {
      const response = await this.requester.get(url, { token });
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e.response.status, error: e };
    }
  }

  /**
   * Retrieve the session details for a given session.
   * @param {Object} params - parameters for the request
   * @param {string} params.installSid - the install Sid
   * @param {string} params.examSid - the exam Sid
   * @param {string} params.sessionSid - the session Sid
   * @returns {Promise<Object>} - a promise that resolves with the session details
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the session details
   * @prop {number} statusCode - the HTTP status code of the response
   * @prop {Object} error - the error object if the request failed
   */
  async getSession(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(API_PATHS.GET_SESSION_DETAILS, pathReplacements);

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  /**
   * Retrieves the course details for a given course.
   * @param {Object} params - parameters for the request
   * @param {string} params.installSid - the install Sid
   * @param {string} params.courseSid - the course Sid
   * @returns {Promise<Object>} - a promise that resolves with the course details
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the course details
   * @prop {number} statusCode - the HTTP status code of the response
   * @prop {Object} error - the error object if the request failed
   */
  async getCourse(params) {
    const pathReplacements = {
      installSid: params.installSid,
      courseSid: params.courseSid,
    };

    const url = this.buildPath(API_PATHS.GET_COURSE_DETAILS, pathReplacements);

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  /**
   * Retrieves the user profile from the API.
   * @param {Object} params - the parameters for the request
   * @param {string} params.installSid - the install Sid
   * @param {string} params.userSid - the user Sid
   * @returns {Promise<Object>} - a promise that resolves with the user profile
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the user profile
   * @prop {number} statusCode - the HTTP status code of the response
   * @prop {Object} error - the error object if the request failed
   */
  async getUserProfile(params) {
    const pathReplacements = {
      installSid: params.installSid,
      userSid: params.userSid,
    };

    const url = this.buildPath(API_PATHS.GET_USER_PROFILE, pathReplacements);

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async getSessionJourney(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    let url = this.buildPath(API_PATHS.GET_SESSION_JOURNEY, pathReplacements);

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  /**
   * Retrieves the annotations for a given session.
   * @param {Object} params - the parameters for the request
   * @param {string} params.installSid - the install Sid
   * @param {string} params.examSid - the exam Sid
   * @param {string} params.sessionSid - the session Sid
   * @returns {Promise<Object>} - a promise that resolves with the annotations
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the annotations
   * @prop {number} statusCode - the HTTP status code of the response
   * @prop {Object} error - the error object if the request failed
   */
  async getAnnotations(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(API_PATHS.GET_ANNOTATIONS, pathReplacements);

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async addAnnotation(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(API_PATHS.ADD_ANNOTATION, pathReplacements);

    try {
      const response = await this.requester.post(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async updateAnnotation(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
      annotationSid: params.annotationSid,
    };

    const url = this.buildPath(API_PATHS.UPDATE_ANNOTATION, pathReplacements);

    try {
      const response = await this.requester.patch(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async deleteAnnotation(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
      annotationSid: params.annotationSid,
    };

    const url = this.buildPath(API_PATHS.DELETE_ANNOTATION, pathReplacements);

    try {
      const response = await this.requester.delete(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  /**
   * Retrieve the list of annotation types for an install.
   * @param {Object} params - parameters for the request
   * @param {string} params.installSid - the install Sid
   * @returns {Promise<Object>} - a promise that resolves with the list of annotation types
   * @prop {boolean} successful - whether the request was successful
   * @prop {Object} data - the annotation types
   * @prop {Object} error - the error object if the request failed
   * @prop {number} statusCode - the HTTP status code of the response
   */
  async getAnnotationTypes(params) {
    const pathReplacements = {
      installSid: params.installSid,
    };

    const url = this.buildPath(
      API_PATHS.GET_ANNOTATION_TYPES,
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async registerParticipant(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.REGISTER_PARTICIPANT,
      pathReplacements
    );

    try {
      const response = await this.requester.post(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async getParticipants(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    let url = this.buildPath(
      API_PATHS.GET_PARTICIPANTS,
      pathReplacements
    );

    // Add query parameters if provided
    const queryParams = [];

    if (params.expand) {
      const expandParam = Array.isArray(params.expand)
        ? params.expand.join(',')
        : params.expand;
      queryParams.push(`expand=${encodeURIComponent(expandParam)}`);
    }

    if (params.type) {
      queryParams.push(`type=${encodeURIComponent(params.type)}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    try {
      const response = await this.requester.get(
        url,
        this.buildHeaders()
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async getRecordingMetadata(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
      token: getMediaAuthToken(this.spToken),
    };

    const url = this.buildPath(
      MEDIA_API_PATHS.GET_RECORDING_METADATA,
      pathReplacements
    );

    try {
      const response = await this.mediaRequester.get(url);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }
  async getModalityOptions(params) {
    const pathReplacements = {
      proctorTypeSubCategorySid: params.proctorTypeSubCategorySid,
    };

    const url = this.buildPath(
      API_PATHS.GET_MODALITY_OPTIONS,
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async addAction(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(API_PATHS.ADD_ACTION, pathReplacements);

    try {
      const response = await this.requester.post(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async createIncidentReport(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.CREATE_INCIDENT_REPORT,
      pathReplacements
    );

    const payload = {
        "subject": params.subject,
        "details": params.details,
        "status": "Open",
        "eventTime": new Date().toISOString(),
        "potentialDamage": params?.potentialDamage || "",
        "correctiveActionDetails": "",
        "contactPerson": "SmarterProctoring",
        "contactPhone": "877-499-7627",
        "contactEmail": "support@smarterproctoring.com",
        "eventTypeSid": "ET0342e95534af42afa27d03f86e32c29d",
        "directWitness": "SmarterProctoring Auditor",
        "draft": params?.draft ?? true,
    };

    try {
      const response = await this.requester.post(url, this.buildHeaders(), payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async updateIncidentReport(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
      eventReportSid: params.eventReportSid,
    };

    const url = this.buildPath(
      API_PATHS.UPDATE_INCIDENT_REPORT,
      pathReplacements
    );

    const payload = {
        "subject": params.subject,
        "details": params.details,
        "status": "Open",
        "eventTime": new Date().toISOString(),
        "potentialDamage": params?.potentialDamage || "",
        "correctiveActionDetails": "",
        "contactPerson": "SmarterProctoring",
        "contactPhone": "877-499-7627",
        "contactEmail": "support@smarterproctoring.com",
        "eventTypeSid": "ET0342e95534af42afa27d03f86e32c29d",
        "directWitness": "SmarterProctoring Auditor",
        "draft": params?.draft ?? true,
    };

    console.log('updateIncidentReport payload:', payload);

    try {
      const response = await this.requester.put(url, this.buildHeaders(), payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async deleteIncidentReport(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
      eventReportSid: params.eventReportSid,
    };

    const url = this.buildPath(
      API_PATHS.DELETE_INCIDENT_REPORT,
      pathReplacements
    );

    try {
      const response = await this.requester.delete(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async listIncidentReports(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      `${API_PATHS.LIST_INCIDENT_REPORTS}?includeDrafts=true`,
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async getSessionPhases(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.GET_SESSION_PHASES,
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async updateSessionReviewStatus(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.UPDATE_SESSION_DETAILS,
      pathReplacements
    );

    try {
      const response = await this.requester.patch(url, this.buildHeaders(), params.payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async closeIncidentReport(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
      eventReportSid: params.eventReportSid,
    };

    const url = this.buildPath(
      API_PATHS.CLOSE_INCIDENT_REPORT,
      pathReplacements
    );

    try {
      const response = await this.requester.put(url, this.buildHeaders(), {});
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async modifySessionJourneyStep(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.SESSION_JOURNEY_STEP,
      pathReplacements
    );

    try {
      const response = await this.requester.post(url, this.buildHeaders(), params.payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async endSession(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.SESSION_END,
      pathReplacements
    );

    try {
      const response = await this.requester.post(url, this.buildHeaders(), params.payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async getSyncDoc(params) {
    const pathReplacements = {
      sessionSid: params.sessionSid,
    };

    const url = this.buildPath(
      API_PATHS.GET_SYNC_DOC,
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async getIdentityValidation(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid
    };

    const url = this.buildPath(
      API_PATHS.IDENTITY_VALIDATION,
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async updateIdentityValidationStatus(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid
    };

    const url = this.buildPath(
      API_PATHS.IDENTITY_VALIDATION,
      pathReplacements
    );

    // paylaod must have status with valid values "pass" or "fail" and must also have a validatedBy value

    if (!params.payload?.status || !params.payload?.validatedBy) {
      throw new Error('Payload must include status (pass/fail) and validatedBy fields');
    }

    if (params.payload.status !== 'pass' && params.payload.status !== 'fail') {
      throw new Error('Status must be either "pass" or "fail"');
    }

    try {
      const response = await this.requester.patch(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async updateMutedAnnotations(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid
    };

    const url = this.buildPath(
      API_PATHS.MUTE_SESSION_ANNOTATIONS,
      pathReplacements
    );

    try {
      const response = await this.requester.put(url, this.buildHeaders(), params.rules);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }


}

export default ApiClient;
